/**
 * 邮件 Mock · 企业邮箱种子数据与本机态操作
 * ------------------------------------------------------------
 * 独立架构域：邮件（M2+ 接企业邮箱服务后，repository 换 HTTP 实现，
 * 本文件只保留 DTO 形状与演示种子）。文件夹为虚拟视图：
 * inbox 收件箱 / sent 已发送 / draft 草稿箱 / trash 回收站；
 * starred 是跨 inbox+sent 的星标筛选，不单独落 folder。
 *
 * 原型期写操作（发送 / 存草稿 / 星标 / 已读 / 移动 / 彻底删除）只改模块内存，
 * 刷新后回种子；与 notify 本机态同口径。
 */

export type MailFolder = 'inbox' | 'sent' | 'draft' | 'trash'

export interface MailAddress {
  name: string
  addr: string
}

export interface MailAttachment {
  name: string
  size: string
}

export interface MailMessage {
  id: string
  folder: MailFolder
  from: MailAddress
  to: MailAddress[]
  cc?: MailAddress[]
  subject: string
  preview: string
  body: string[]
  time: string
  ts: number
  unread: boolean
  starred: boolean
  attachments?: MailAttachment[]
}

/** 写信入参（页面收集，地址由字符串解析） */
export interface MailDraftInput {
  to: MailAddress[]
  cc?: MailAddress[]
  subject: string
  body: string[]
}

/** 当前登录人（演示账号：马剑）；M2+ 从会话资料取 */
export const MAIL_ME: MailAddress = { name: '马剑', addr: 'majian@ema-aierp.cn' }

const DAY = 86400000

/* 种子邮件：收件箱 9 + 已发送 2 + 草稿 1，时间按相对今天偏移，保证演示时「今天/昨天」语义稳定 */
const MAIL_SEED: MailMessage[] = [
  {
    id: 'm01', folder: 'inbox',
    from: { name: '财务共享中心', addr: 'finance@ema-aierp.cn' },
    to: [MAIL_ME],
    subject: '9 月费用报销单已审批通过（BX-202609-0182）',
    preview: '您提交的 9 月差旅费报销单已完成全部审批，金额 ¥3,486.00，预计 10 月 15 日随薪酬一并发放。',
    body: [
      '马剑，您好：',
      '您于 9 月 28 日提交的差旅费报销单（单号 BX-202609-0182，金额 ¥3,486.00）已于 10 月 16 日完成全部审批节点。',
      '款项预计在 10 月 15 日薪酬发放日一并支付到您的工资卡，届时请查收。电子审批单与发票影像可在「财务自助 → 报销查询」中下载。',
      '如有疑问，请回复本邮件或联系财务共享中心（分机 8012）。'
    ],
    time: '今天 09:12', ts: Date.now() - 2 * 3600000,
    unread: true, starred: true,
    attachments: [{ name: '报销审批单-BX-202609-0182.pdf', size: '186 KB' }]
  },
  {
    id: 'm02', folder: 'inbox',
    from: { name: 'EMA 平台管理员', addr: 'noreply@ema-aierp.cn' },
    to: [MAIL_ME],
    subject: '【安全提醒】您的账号于新设备登录',
    preview: '系统检测到您的 EMA 账号在 Windows 设备（上海）登录，如非本人操作请立即修改密码。',
    body: [
      '您好：',
      '系统检测到您的 EMA 账号于今天 08:47 在一台 Windows 设备（IP 归属地：上海）完成登录。',
      '如果是您本人操作，请忽略本邮件；如非本人操作，请立即进入「设置中心 → 账号安全」修改密码并联系信息化部。'
    ],
    time: '今天 08:47', ts: Date.now() - 3.5 * 3600000,
    unread: true, starred: false
  },
  {
    id: 'm03', folder: 'inbox',
    from: { name: '江苏华盛精密制造', addr: 'liwei@huasheng-mfg.com' },
    to: [MAIL_ME],
    cc: [{ name: '采购部 · 周明', addr: 'zhouming@ema-aierp.cn' }],
    subject: '关于采购订单 PO-2026-1142 的确认回复',
    preview: '我司已确认订单数量与交期：第一批 10 月 24 日到货，第二批 11 月 5 日到货，随货附质检报告。',
    body: [
      '马经理，您好：',
      '收到贵司采购订单 PO-2026-1142，我司已评审确认，交付安排如下：',
      '第一批（60%）：10 月 24 日送达贵司一号仓，随货附出厂质检报告；第二批（40%）：11 月 5 日送达。',
      '账期按合同执行（月结 60 天）。如收货安排有调整请提前 2 个工作日告知。'
    ],
    time: '昨天 16:30', ts: Date.now() - DAY - 7 * 3600000,
    unread: true, starred: false,
    attachments: [{ name: 'PO-2026-1142-确认回签.pdf', size: '312 KB' }]
  },
  {
    id: 'm04', folder: 'inbox',
    from: { name: '人力资源部', addr: 'hr@ema-aierp.cn' },
    to: [MAIL_ME],
    subject: '10 月薪酬发放与考勤核对通知',
    preview: '10 月工资将于 15 日发放，请于 13 日前在「人力自助」完成考勤异常核对，逾期视为确认无误。',
    body: [
      '各位同事：',
      '10 月薪酬将于 10 月 15 日按时发放。请于 10 月 13 日 18:00 前登录「人力自助」核对 9 月 26 日—10 月 10 日的考勤记录。',
      '如有漏打卡、请假未销假等异常，请在线提交核对申请，逾期未核对视为确认无误。'
    ],
    time: '昨天 11:05', ts: Date.now() - DAY - 12 * 3600000,
    unread: false, starred: false
  },
  {
    id: 'm05', folder: 'inbox',
    from: { name: '信息化部 · 王浩然', addr: 'wanghaoran@ema-aierp.cn' },
    to: [MAIL_ME],
    cc: [{ name: '研发组全员', addr: 'dev@ema-aierp.cn' }],
    subject: '采购移动端需求评审会议纪要（10/16）',
    preview: '会议确认移动端收货确认 10-14 启动开发，扫码枪蓝牙协议由集成组郑爽对接，详见纪要中的三项决议。',
    body: [
      '各位好，',
      '今天上午的采购移动端需求评审纪要已整理，关键决议如下：',
      '1. 移动端收货确认页 10-14 启动、10-24 完成开发，默认全量收货、可改数量并留痕；',
      '2. 扫码枪对接走客户现有蓝牙协议，由集成组郑爽负责，10-21 进场联调；',
      '3. 仓库角色新增移动端收货权限点，随任务授予回收（周明）。',
      '如有补充请在本周内回复。'
    ],
    time: '昨天 10:20', ts: Date.now() - DAY - 13 * 3600000,
    unread: false, starred: true
  },
  {
    id: 'm06', folder: 'inbox',
    from: { name: '异常处置系统', addr: 'alert@ema-aierp.cn' },
    to: [MAIL_ME],
    subject: '【超期预警】异常单 EX-2026-1017-006 即将超 SLA',
    preview: '收货容差差异异常单剩余处理时限 6 小时，当前节点：差异分析。请及时跟进或申请升级。',
    body: [
      '预警通知：',
      '异常单 EX-2026-1017-006（采购收货容差差异）剩余处理时限不足 6 小时，当前停留在「差异分析」节点。',
      '请责任人及时跟进；确需延期的，请在异常台账中提交升级申请并注明原因。'
    ],
    time: '周二 15:44', ts: Date.now() - 2 * DAY + 3600000,
    unread: false, starred: false
  },
  {
    id: 'm07', folder: 'inbox',
    from: { name: '行政部', addr: 'admin@ema-aierp.cn' },
    to: [MAIL_ME],
    subject: '关于 10 月全员例会改期的通知',
    preview: '原 10 月 30 日全员例会因场地调整改至 10 月 31 日 14:00，地点不变（三楼多功能厅）。',
    body: [
      '各位同事：',
      '原定 10 月 30 日的月度全员例会因场地冲突调整至 10 月 31 日（周六）14:00，地点仍为三楼多功能厅。',
      '请各部门提前协调值班安排，会议材料于会前一天发送至各部门接口人。'
    ],
    time: '周一 17:20', ts: Date.now() - 3 * DAY,
    unread: false, starred: false
  },
  {
    id: 'm08', folder: 'inbox',
    from: { name: '宁波恒泰包装', addr: 'sales@hengtai-pack.com' },
    to: [MAIL_ME],
    subject: '报价单 HT-Q2026-093（纸箱与缓冲材料）',
    preview: '按贵司询价要求更新四季度报价：同款纸箱单价下调 3.2%，报价有效期至 10 月 31 日，详见附件。',
    body: [
      '马经理，您好：',
      '感谢再次询价。按四季度原材料价格，同款瓦楞纸箱单价较上季度下调 3.2%，缓冲材料价格持平，详见附件报价单。',
      '报价有效期至 10 月 31 日，期待合作。'
    ],
    time: '周一 09:30', ts: Date.now() - 3 * DAY - 8 * 3600000,
    unread: false, starred: false,
    attachments: [{ name: 'HT-Q2026-093-报价单.xlsx', size: '94 KB' }]
  },
  {
    id: 'm09', folder: 'inbox',
    from: { name: '信息化部', addr: 'it@ema-aierp.cn' },
    to: [MAIL_ME],
    subject: 'EMA 平台操作培训报名（10 月 23 日专场）',
    preview: '本周五下午开设「架构浏览 + 异常处置」实操专场，限额 30 人，点击邮件内链接报名。',
    body: [
      '各位同事：',
      '本周五（10 月 23 日）15:00–17:00，信息化部在二楼培训室开设 EMA 平台实操专场，内容：架构资产浏览、异常台账处置、页签与全局搜索技巧。',
      '本场限额 30 人，报名链接见部门公告。建议新接入业务部门的关键用户参加。'
    ],
    time: '10-12 14:00', ts: Date.now() - 5 * DAY,
    unread: false, starred: false
  },
  {
    id: 's01', folder: 'sent',
    from: MAIL_ME,
    to: [{ name: '李桂英', addr: 'liguiying@ema-aierp.cn' }],
    subject: 'Re：入库接口联调时间确认',
    preview: '周三上午 10 点开始联调，我这边让王浩然一起参加，接口文档以最新版 wiki 为准。',
    body: [
      '桂英，好的：',
      '周三上午 10 点准时开始，我拉上浩然一起。接口字段以 wiki 上 10-14 更新的版本为准，联调环境账号我稍后发你。'
    ],
    time: '周二 18:02', ts: Date.now() - 2 * DAY - 2 * 3600000,
    unread: false, starred: false
  },
  {
    id: 's02', folder: 'sent',
    from: MAIL_ME,
    to: [{ name: '江苏华盛精密制造 · 李伟', addr: 'liwei@huasheng-mfg.com' }],
    subject: '采购订单 PO-2026-1142 请确认',
    preview: '订单及交付要求见附件，请于本周内回签确认，第一批希望 10 月 24 日前到货。',
    body: [
      '李经理，您好：',
      '采购订单 PO-2026-1142 已发送（见附件），请安排评审并于本周内回签。第一批希望能在 10 月 24 日前送达一号仓。',
      '有任何问题随时联系。'
    ],
    time: '周一 15:40', ts: Date.now() - 3 * DAY - 2 * 3600000,
    unread: false, starred: false,
    attachments: [{ name: 'PO-2026-1142.pdf', size: '128 KB' }]
  },
  {
    id: 'd01', folder: 'draft',
    from: MAIL_ME,
    to: [{ name: '信息化部 · 王浩然', addr: 'wanghaoran@ema-aierp.cn' }],
    subject: '交付观察期数据口径（草稿）',
    preview: '观察期到期判定想和你再对一下：灰度试点的转化数据按天还是按批次统计？',
    body: [
      '浩然，',
      '观察期到期判定想和你再对一下：灰度试点的转化数据按天还是按批次统计？另外「未达目标回流」触发后，原案例的观察记录是否保留？'
    ],
    time: '草稿 · 今天 09:40', ts: Date.now() - 1.5 * 3600000,
    unread: false, starred: false
  }
]

/* 模块内存态（原型期；F5 回种子） */
let mails: MailMessage[] = MAIL_SEED.map(m => ({ ...m, to: [...m.to], body: [...m.body] }))
let seq = 100

export function mailList(): MailMessage[] {
  return mails
}

export function mailPatch(id: string, patch: Partial<Pick<MailMessage, 'unread' | 'starred'>>): void {
  const m = mails.find(x => x.id === id)
  if (m) Object.assign(m, patch)
}

/** 移动到指定文件夹（trash 内再删走恢复语义由调用方传 inbox） */
export function mailMove(id: string, folder: MailFolder): void {
  const m = mails.find(x => x.id === id)
  if (m) m.folder = folder
}

/** 彻底删除（回收站用） */
export function mailRemove(id: string): void {
  mails = mails.filter(m => m.id !== id)
}

export function mailSend(input: MailDraftInput): MailMessage {
  const m: MailMessage = {
    id: 's' + seq++, folder: 'sent',
    from: MAIL_ME, to: input.to, cc: input.cc,
    subject: input.subject || '（无主题）',
    preview: input.body[0] || '', body: input.body,
    time: '刚刚', ts: Date.now(), unread: false, starred: false
  }
  mails.unshift(m)
  return m
}

/** 新建草稿（id 缺省）或更新既有草稿（回复/编辑） */
export function mailSaveDraft(input: MailDraftInput, id?: string): MailMessage {
  const existing = id ? mails.find(m => m.id === id) : undefined
  if (existing) {
    Object.assign(existing, { to: input.to, cc: input.cc, subject: input.subject, preview: input.body[0] || '', body: input.body })
    return existing
  }
  const m: MailMessage = {
    id: 'd' + seq++, folder: 'draft',
    from: MAIL_ME, to: input.to, cc: input.cc,
    subject: input.subject || '（无主题）',
    preview: input.body[0] || '', body: input.body,
    time: '草稿 · 未发送', ts: Date.now(), unread: false, starred: false
  }
  mails.unshift(m)
  return m
}

/* ---------- 收件箱语义归类 ----------
 * 原型期为规则归类（外部发件域 + 主题/摘要关键词），只作用于收件箱；
 * 规则未命中的邮件不进任何分类，留在收件箱原列表，不硬凑进"其他"。
 * M2+ 由企业邮箱服务端的语义模型产出 category 字段随 DTO 下发，本函数与 UI 口径同步替换。 */
export type MailCategory = 'approval' | 'customer' | 'meeting' | 'notice' | 'system'

export const MAIL_CATEGORY_LABEL: Record<MailCategory, string> = {
  approval: '审批流程',
  customer: '业务往来',
  meeting: '会议纪要',
  notice: '通知公告',
  system: '系统提醒'
}

export function mailClassify(m: MailMessage): MailCategory | null {
  if (m.folder !== 'inbox') return null
  const text = m.subject + ' ' + m.preview
  if (!m.from.addr.endsWith('@ema-aierp.cn')) return 'customer'
  if (/会议纪要|评审纪要/.test(text)) return 'meeting'
  if (/审批|报销|单据/.test(text)) return 'approval'
  if (/noreply@|alert@/.test(m.from.addr) || /【.*(提醒|预警|告警)】|超期|SLA/.test(text)) return 'system'
  if (/通知|公告|报名|例会|薪酬|考勤|培训/.test(text)) return 'notice'
  return null
}

/* ---------- 第三方邮箱绑定（邮箱管理） ----------
 * 与邮件同口径：原型期绑定/解绑/重验只改模块内存，F5 回种子；
 * M2+ 接企业邮箱服务后由服务端持有凭据与同步状态，本文件仅保留 DTO 与演示种子。
 * 授权码（authCode）是第三方邮箱后台生成的客户端专用口令，非登录密码。 */
export type MailProvider = 'qq' | 'netease' | 'gmail' | 'outlook' | 'imap'

export const MAIL_PROVIDER_LABEL: Record<MailProvider, string> = {
  qq: 'QQ 邮箱',
  netease: '网易邮箱',
  gmail: 'Gmail',
  outlook: 'Outlook',
  imap: '其他 IMAP'
}

export interface MailAccount {
  id: string
  provider: MailProvider
  email: string
  /** active 已连接 / error 验证失败（如授权码过期），失败原因在 err */
  st: 'active' | 'error'
  lastSync: string
  err?: string
}

export interface MailAccountInput {
  provider: MailProvider
  email: string
  /** 第三方邮箱授权码；原型期仅做非空校验，不真实连接外部服务器 */
  authCode: string
  /** 其他 IMAP 邮箱需手工填写服务器 */
  imapHost?: string
  imapPort?: string
}

const MAIL_ACCOUNT_SEED: MailAccount[] = [
  { id: 'a01', provider: 'qq', email: 'majian@qq.com', st: 'active', lastSync: '今天 08:30' },
  { id: 'a02', provider: 'netease', email: 'majian1988@163.com', st: 'error', lastSync: '昨天 19:12', err: '授权码已过期，请重新验证' }
]

let accounts: MailAccount[] = MAIL_ACCOUNT_SEED.map(a => ({ ...a }))
let acctSeq = 100

export function mailAccountList(): MailAccount[] {
  return accounts
}

/** 绑定第三方邮箱；重复绑定按业务异常抛出，由调用方就地提示 */
export function mailAccountBind(input: MailAccountInput): MailAccount {
  const dup = accounts.find(a => a.email.toLowerCase() === input.email.toLowerCase())
  if (dup) throw new Error('该邮箱已绑定，请勿重复添加')
  const a: MailAccount = {
    id: 'a' + acctSeq++,
    provider: input.provider,
    email: input.email,
    st: 'active',
    lastSync: '刚刚'
  }
  accounts.push(a)
  return a
}

export function mailAccountUnbind(id: string): void {
  accounts = accounts.filter(a => a.id !== id)
}

/** 重新验证（原型期置为已连接并刷新同步时间） */
export function mailAccountVerify(id: string): void {
  const a = accounts.find(x => x.id === id)
  if (a) { a.st = 'active'; a.err = undefined; a.lastSync = '刚刚' }
}
